import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import BaseLayout from './BaseLayout';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import {
  EMPTY_REGISTRATION,
  buildRegistrationRequest,
  signUpErrorMessage,
} from './authForm';
import '../styles/Register.css';

const Register = () => {
  const { isAuthenticated } = useAuth();
  const [formData, setFormData] = useState(EMPTY_REGISTRATION);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The address a confirmation was requested for. Set on the 202, which the
  // server gives identically for a new address and a taken one, so the screen
  // it switches to must be true of both.
  const [sentTo, setSentTo] = useState('');

  // Redirects before rendering, matching Login. A signed-in user reaching
  // /register was previously offered a second account.
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear this field's error as soon as it is edited. The old form set a
    // "Passwords do not match" message that nothing ever cleared, so it sat
    // above the corrected fields until the next submit.
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Without this, a double click posts twice. The second request loses the
    // unique-constraint race and reports "username taken" for the account
    // the first one just created successfully.
    if (isSubmitting) return;

    const { body, errors } = buildRegistrationRequest(formData);
    if (errors) {
      setFieldErrors(errors);
      setError('');
      return;
    }

    setIsSubmitting(true);
    setFieldErrors({});
    setError('');
    try {
      await axios.post(buildApiUrl('/users/register'), body);

      // Not "account created", and not straight to sign-in. The server gives
      // this same answer when the address already has an account, so the
      // screen cannot know an account was made — and a new one cannot sign in
      // until its owner opens the link we just emailed (AUTH-30).
      setSentTo(body.email);
    } catch (err) {
      setError(signUpErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (sentTo) {
    return (
      <BaseLayout>
        <div className="register">
          <div className="card register-card">
            <header className="register-header">
              <span className="pill">Create account</span>
              <h1 className="page-title register-title">Check your email</h1>
            </header>
            <div className="status-success" role="status">
              We sent a message to <strong>{sentTo}</strong>. Open the link in it to
              finish signing up.
            </div>
            {/* Both remedies, because the screen cannot tell which applies:
                the mail may be in spam, or the address may already have an
                account, in which case the mail says so and reset is the way
                in. Naming only one would guess, and guessing right would
                leak. */}
            <p className="meta-copy register-subtitle">
              Nothing after a few minutes? Check your spam folder. If you already
              have an account with this address, reset your password instead.
            </p>
            <div className="register-actions">
              <Link to="/login" className="btn-primary">
                Go to sign in
              </Link>
              <Link to="/forgot-password" className="btn-ghost">
                Reset my password
              </Link>
            </div>
          </div>
        </div>
      </BaseLayout>
    );
  }

  /** A field and its error, wired up for assistive tech. */
  const field = ({ name, label, type, autoComplete, placeholder }) => {
    const id = `register-${name}`;
    const errorId = `${id}-error`;
    const message = fieldErrors[name];

    return (
      <div className="register-field">
        {/* htmlFor/id: the old labels wrapped nothing and named nothing, so a
            screen reader announced four unlabelled text boxes. */}
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          className="field"
          type={type}
          name={name}
          autoComplete={autoComplete}
          value={formData[name]}
          onChange={handleChange}
          placeholder={placeholder}
          aria-invalid={message ? 'true' : undefined}
          aria-describedby={message ? errorId : undefined}
        />
        {message && (
          <p className="register-field-error" id={errorId}>
            {message}
          </p>
        )}
      </div>
    );
  };

  return (
    <BaseLayout>
      <div className="register">
        <div className="card register-card">
          <header className="register-header">
            <span className="pill">Create account</span>
            <h1 className="page-title register-title">Start training</h1>
            <p className="meta-copy register-subtitle">
              A training log for structured, prescribed work.
            </p>
          </header>

          {error && (
            <div className="status-error" role="alert">
              {error}
            </div>
          )}

          {/* noValidate: the browser's own bubbles cannot be styled, are not
              announced consistently, and stop at the first invalid field.
              validateRegistrationForm reports every problem at once. */}
          <form className="register-form" onSubmit={handleSubmit} noValidate>
            {field({
              name: 'username',
              label: 'Username',
              type: 'text',
              autoComplete: 'username',
              placeholder: 'Choose a username',
            })}
            {field({
              name: 'email',
              label: 'Email',
              type: 'email',
              autoComplete: 'email',
              placeholder: 'you@example.com',
            })}
            {/* new-password, not current-password: this tells a password
                manager to offer to generate and save one. The old form had no
                autocomplete at all, so managers stayed silent. */}
            {field({
              name: 'password',
              label: 'Password',
              type: 'password',
              autoComplete: 'new-password',
              placeholder: 'At least 6 characters',
            })}
            {field({
              name: 'confirmPassword',
              label: 'Confirm password',
              type: 'password',
              autoComplete: 'new-password',
              placeholder: 'Repeat your password',
            })}

            <div className="register-actions">
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Creating account…' : 'Create account'}
              </button>
              <Link to="/login" className="btn-ghost register-signin">
                Sign in instead
              </Link>
            </div>
          </form>
        </div>
      </div>
    </BaseLayout>
  );
};

export default Register;
